import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { UpdateOptions, VehicleSearchParams } from 'interface';
import type { RootState } from '../store';

export const initialState: VehicleSearchParams = {
  id: 0,
  duration: 0,
  include_maintenance: false,
  initial_payment: 0,
  yearly_mileage: 0,
  optionsSelected: [],
  optionsAdded: null,
};

const updateSearchParams = (state: VehicleSearchParams) => {
  var searchParams = new URLSearchParams(window.location.search);
  const includeMaintenance = state.include_maintenance
    ? `,include_maintenance=${state.include_maintenance}`
    : '';
  searchParams.set(
    'calculationParameters',
    `duration=${state.duration},yearly_mileage=${state.yearly_mileage},initial_payment=${state.initial_payment}${includeMaintenance}`
  );

  if (Number(state.optionsSelected?.length) > 0) {
    searchParams.set(
      'optionsSelected',
      state.optionsSelected ? state.optionsSelected.join() : ', '
    );
  }

  if (state.optionsAdded != null) {
    searchParams.set(
      'optionsAdded',
      state.optionsAdded ? state.optionsAdded.toString() : ''
    );
  }

  history.pushState(null, '', `${window.location.pathname}?${searchParams}`);
};

export const vehicleSearchParamsSlice = createSlice({
  name: 'vehicleSearchParams',
  initialState: initialState,
  reducers: {
    updateAllParams: (state, action: PayloadAction<VehicleSearchParams>) => {
      state.id = action.payload.id;
      state.duration = action.payload.duration;
      state.include_maintenance = action.payload.include_maintenance;
      state.initial_payment = action.payload.initial_payment;
      state.yearly_mileage = action.payload.yearly_mileage;
      state.optionsSelected = action.payload.optionsSelected;
      state.optionsAdded = action.payload.optionsAdded;
    },
    updateVehicleId: (state, action: PayloadAction<number>) => {
      state.id = action.payload;
    },
    updateDuration: (state, action: PayloadAction<number>) => {
      state.duration = action.payload;
      updateSearchParams(state);
    },
    updateIncludeMaintenance: (state, action: PayloadAction<boolean>) => {
      state.include_maintenance = action.payload;
      updateSearchParams(state);
    },
    updateInitialPayment: (state, action: PayloadAction<number>) => {
      state.initial_payment = action.payload;
      updateSearchParams(state);
    },
    updateYearlyMileage: (state, action: PayloadAction<number>) => {
      state.yearly_mileage = action.payload;
      updateSearchParams(state);
    },
    updateSelectedOptions: (state, action: PayloadAction<number[] | null>) => {
      state.optionsSelected = action.payload ? action.payload : null;
    },
    updateAddedOptions: (state, action: PayloadAction<number | null>) => {
      state.optionsAdded = action.payload ? action.payload : null;
    },
    updateMandatoryOptionParam: (
      state,
      action: PayloadAction<UpdateOptions>
    ) => {
      state.optionsSelected = state.optionsSelected?.filter(
        (option) => option !== action.payload.removeOption
      );
      state.optionsSelected?.push(action.payload.addOption);
    },
    removeOptionParam: (state, action: PayloadAction<number>) => {
      state.optionsSelected = state.optionsSelected?.filter(
        (option) => option !== action.payload
      );
      state.optionsAdded =
        state.optionsAdded !== action.payload ? state.optionsAdded : null;
    },
    updateAddedOptionParam: (state, action: PayloadAction<number>) => {
      state.optionsAdded = action.payload;
    },
    updateSelectedOptionsParams: (state) => {
      updateSearchParams(state);
    },
    updateAddedOptionsParams: (state) => {
      updateSearchParams(state);
    },
  },
});

export const {
  updateAllParams,
  updateVehicleId,
  updateDuration,
  updateIncludeMaintenance,
  updateInitialPayment,
  updateYearlyMileage,
  updateMandatoryOptionParam,
  updateAddedOptionParam,
  removeOptionParam,
  updateSelectedOptionsParams,
  updateAddedOptionsParams,
  updateSelectedOptions,
  updateAddedOptions,
} = vehicleSearchParamsSlice.actions;

export const vehiclePricingConfig = (state: RootState) =>
  state.vehicleSearchParams;

export default vehicleSearchParamsSlice.reducer;
